<template>
  <div class="pa-10">
    <v-row>
      <v-col cols="9">
        <v-row>
          <h2 v-if="onlyAgent">Learner: {{ agentName }}</h2>
          <h2 v-else>Scenario: {{ isPublic(course) ? course.name  + " (Public) " : course.name }}</h2>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                color="gray"
                v-bind="attrs"
                v-on="on"
                @click.prevent="refresh()"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </template>
            <span>Refresh</span>
          </v-tooltip>
        </v-row>
      </v-col>
      <v-col cols="3" v-if="onlyAgent">
        <v-select
          :items="selectItems"
          item-text="name"
          item-value="id"
          :label="selected"
          :loading="loadingCourse"
          :no-data-text="noDataText"
          @change="onCourseChange"
        ></v-select>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col :cols="isPublic(course) ? '12' : '6'">
        <v-card class="mx-auto card-body" style="height:380px">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" class="title blue--text">Overview</v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-row no-gutters>
              <v-col cols="6" class="caption font-weight-light">
                <v-row>
                  <data-overview
                    :course="course"
                    :showPublishDate="true"
                    :showAvgInteractions="false"
                    :agent="agentId"
                  />
                </v-row>
              </v-col>
              <v-col cols="6" class="body-1 font-weight-light">
                <v-row>
                  <v-col cols="12"
                    ><v-row v-if="!onlyAgent" class="justify-center"
                      >Scores</v-row
                    ></v-col
                  >
                </v-row>
                <v-row class="justify-center">
                  <!-- display passed or failed if we are showing agent -->
                  <div v-if="onlyAgent">
                    <v-icon
                      v-if="agentStatus == 'passed'"
                      color="rgb(126, 211, 33)"
                      :style="pieCheckSize"
                      >mdi-check-circle</v-icon
                    >
                    <v-icon
                      v-if="agentStatus == 'failed'"
                      color="rgb(245, 166, 35)"
                      :style="pieCheckSize"
                      >mdi-close-circle</v-icon
                    >
                  </div>
                  <score-pie-chart
                    v-else
                    :chartStyle="myStyles"
                    :course="course"
                  />
                </v-row>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="!isPublic(course)" cols="6">
        <v-card class="mx-auto card-body" style="height:380px">
          <v-card-text>
            <v-row dense>
              <v-col cols="10" class="title blue--text"
                >Completion Status</v-col
              >
              <v-col
                v-if="assignedAgents != 0"
                cols="2"
                class="section-stats-display justify-left"
                ><v-tooltip top>
                  <template v-slot:activator="{ on, attrs }"
                    ><v-icon color="gray" v-bind="attrs" v-on="on"
                      >mdi-account</v-icon
                    ><span>{{ assignedAgents }}</span>
                  </template>
                  <span>{{ assignedAgents }} Learners Assigned</span>
                </v-tooltip></v-col
              >
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <completion-status :course="course" :style="myStyles" />
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-card class="mx-auto card-body">
          <v-card-text>
            <v-row dense>
              <v-col cols="10" class="title blue--text"
                >Experience Timeline</v-col
              >
              <v-col cols="2" class="d-flex title blue--text justify-end"
                ><v-select
                  :items="timeOptions"
                  item-text="name"
                  item-value="name"
                  v-model="timeOption"
                  :label="timelineSelected"
                  @change="onTimeLine"
                ></v-select
              ></v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <timeline-line-chart
              :course="course"
              :agent="agentId"
              :selectedOption="timeOption"
            />
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-card class="mx-auto card-body" style="height:380px">
          <v-card-text>
            <v-row dense>
              <v-col cols="6" class="title blue--text">Leaderboard</v-col>
              <v-col cols="6" class="font-weight-light">
                <v-tabs v-model="tabSelected" right>
                  <v-tab v-for="item in tabsItems" :key="item">
                    {{ item }}
                  </v-tab>
                </v-tabs>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <leader-board
              :course="course"
              yLabel="Time Spent"
              xLabel="Interactions"
              :agent="agentId"
              :tabSelected="tabSelected"
            />
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-card v-if="!onlyAgent" class="mx-auto card-body">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" class="title blue--text section-title"
                >Scenes</v-col
              >
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-expansion-panels multiple>
              <v-expansion-panel
                v-for="item in sections"
                :key="item.section"
                style="min-height: 90px;"
              >
                <v-expansion-panel-header expand-icon="mdi-menu-down">
                  <v-row no-gutters>
                    <v-col cols="6" class="section-item-title">
                      {{ item.section }}
                    </v-col>
                    <v-col cols="2" class="section-stats-display"
                      ><v-icon color="gray">schedule</v-icon
                      ><span>{{ item.duration }}</span></v-col
                    >
                    <v-col
                      cols="2"
                      v-if="item.questions > 0"
                      class="section-stats-display"
                      ><v-icon color="gray">mdi-forum</v-icon
                      ><span>{{ item.questions }}</span></v-col
                    >
                    <v-col
                      cols="2"
                      v-if="item.hotspots > 0"
                      class="section-stats-display"
                      ><v-icon color="gray">mdi-fire</v-icon
                      ><span>{{ item.hotspots }}</span></v-col
                    >
                  </v-row>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <interaction-chart
                    :activities="item.activities"
                    :course="course"
                    :duration="item.duration"
                  />
                </v-expansion-panel-content>
                <v-expansion-panel-content
                  v-for="act in item.activities"
                  :key="act.name"
                  ><choice-interaction
                    v-if="act.type === 'choice'"
                    :activity="act"
                    :course="course"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense v-if="hasVariables">
      <v-col cols="12">
        <v-card class="mx-auto card-body">
          <v-card-text>
            <v-row dense>
              <v-col cols="12" class="title blue--text section-title"
                >Variables</v-col
              >
            </v-row>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-row v-if="agentId == null">
              <v-col
                v-for="[key, value] in variables.entries()"
                :key="value"
                cols="12"
                xs="6"
                sm="6"
                md="6"
                lg="4"
                xl="3"
              >
                <variable-pie
                  :variables="value"
                  :variableName="key"
                  :course="course"
                  :agent="agentId"
                  :chartStyle="myStyles"
                  :selectedOption="timeOption"
                />
              </v-col>
            </v-row>
            <v-row v-else>
              <v-col>
                <variable-List
                  :variables="variables"
                  :course="course"
                  :agent="agentId"
                  :selectedOption="timeOption"
                />
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <data-log
          style="width:100%"
          :headers="headers"
          :logData="tableData"
          :title="tableTitle"
          :isLoading="loading"
          :csvFilename="csvFilename"
          :itemsPerPage="12"
          :courseId="course.id"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import BubbleChart from '@/components/BubbleChart.vue'
import ChoiceInteraction from '@/components/ChoiceInteraction.vue'
import InteractionChart from '@/components/InteractionChart.vue'
import CompletionStatus from '@/components/CompletionStatus.vue'
import LeaderBoard from '@/components/LeaderBoard.vue'
import TimelineLineChart from '@/components/TimelineLineChart.vue'
import DataOverview from '@/components/DataOverview.vue'
import ScorePieChart from '@/components/ScorePieChart.vue'
import VariablePie from '@/components/VariablePie.vue'
import VariableList from '@/components/VariableList.vue'
import DataLog from '@/components/DataLog.vue'
import moment from 'moment'
import _ from 'lodash'
import store from '@/store/index'

import {
  getDuration,
  getUrlParameter,
  interactionDetailTableHeader,
  interactionTitle,
  timelineTitle,
  Multiset,
  csvExport,
  pieChartStyle,
  sortDataByDate,
  getDateOptionsFromInteractions
} from '../Constants'

function getAgentCourse(routeTo, next) {
  const agentId = parseInt(routeTo.query.agentId) || 0
  const courseId = parseInt(routeTo.params.courseid)
  store.dispatch('course/fetchCourse', courseId).then(course => {
    routeTo.params.course = course
    routeTo.params.courseId = courseId
    routeTo.query.agentId = agentId
    next()
  })
}

export default {
  props: {
    course: {
      type: Object,
      required: true
    },
    courseid: {
      type: String,
      required: true
    }
  },
  beforeRouteEnter(routeTo, routeFrom, next) {
    getAgentCourse(routeTo, next)
  },
  beforeRouteUpdate(routeTo, routeFrom, next) {
    getAgentCourse(routeTo, next)
  },
  components: {
    ChoiceInteraction,
    InteractionChart,
    DataOverview,
    ScorePieChart,
    LeaderBoard,
    TimelineLineChart,
    DataLog,
    CompletionStatus,
    VariablePie,
    VariableList
  },
  data() {
    return {
      agentId: null,
      tableData: [],
      tableTitle: interactionTitle,
      headers: interactionDetailTableHeader,
      scoredLeader: false,
      leaderboardTitle: 'Interactions Leaderboard',
      select: 'Interactions, Time Spent',
      sections: null,
      options: {},
      height: 200,
      agentStatus: null,
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      search: '',
      selectItems: null,
      selected: null,
      loading: false,
      csvFilename: 'interactions',
      allCourses: false,
      agentName: null,
      tabSelected: 1,
      tabsItems: ['Score', 'Time Spent'],
      assignedAgents: 0,
      timeOptions: ['all'],
      timeOption: {},
      timelineSelected: '',
      loadingCourse: false,
      noDataText: 'No Scenarios Available',
      variables: null,
      hasVariables: false
    }
  },
  mounted() {
    if (this.agentId != null) {
      this.loadingCourse = true
      this.noDataText = 'Loading ...'
      this.selected = `Viewing: ${this.course.name}`
      this.selectItems = []
      this.$store
        .dispatch('course/fetchAgentCourse', {
          id: this.agentId,
          name: this.agentName
        })
        .then(response => {
          this.selectItems = Object.assign([], response)
          this.selectItems.splice(0, 0, { id: -1, name: 'All' })
        })
        .catch(err => {})
        .finally(() => {
          this.loadingCourse = false
          this.noDataText = 'No Scenarios Available'
        })
    }
    this.setData()
  },
  created() {
    const id = this.$route.query.agentId
    const name = this.$route.query.agentName
    if (id !== undefined && id != null && id !== 0) {
      this.agentId = '' + id
    }

    if (name !== undefined && name != null && name !== '') {
      this.agentName = name
    }
  },
  methods: {
    isPublic(course) {
      return course && course.courseId.includes('/public')
    },
    refresh() {
      this.$router.go()
    },
    downloadData() {
      const courseName = this.course.name.replace(/\s+/g, '_')
      const csvFilename = courseName + '_interaction' + '_log'
      csvExport(this.headers, this.tableData, csvFilename)
    },
    getColor(response) {
      if (response === 'Correct') return 'green--text'
      else return 'orange--text'
    },
    onTimeLine(value) {
      this.timeOption = _.find(this.timeOptions, {
        name: value
      })
    },
    onCourseChange(value) {
      // all was selected
      if (value === -1) {
        this.allCourses = true
        this.selected = `Loading ... All`
        this.$router.replace({ path: `/agent/${this.agentId}` })
      } else {
        //const courseName = _.find(this.selectItems, { id: value }).name
        this.selected = `Loading ... ${this.course.name}`
        this.$router.replace({
          path: `/courses/${value}?agentId=${
            this.agentId
          }&agentName:${encodeURIComponent(this.agentName)}`
        })
      }
    },
    interactionChange(value) {
      this.scoredLeader = this.select.includes('Score')
      this.leaderboardTitle = this.scoredLeader
        ? 'Score Leaderboard'
        : 'Interactions Leaderboard'
    },
    goBack() {
      this.$router.push('/')
    },
    setData() {
      this.loading = true
      this.sections = new Array()
      this.tableData = new Array()
      // let leaderboard = new Array(
      //   { name: 'time', interactions: [] },
      //   { name: 'score', interactions: [] }
      // )

      if (
        this.course.completionStatus &&
        this.course.completionStatus.assigned
      ) {
        this.assignedAgents = this.course.completionStatus.assigned.length
      }

      let interactions = Object.assign([], this.course.interactions)
      let results = this.course.interactions.filter(
        val => val.result && val.result.extensions
      )
      this.variables = new Multiset()
      for (let i = 0; i < results.length; i++) {
        let result = results[i].result
        if (result.extensions['http://adlnet.gov/expapi/extension/variables']) {
          for (
            let x = 0;
            x <
            result.extensions['http://adlnet.gov/expapi/extension/variables']
              .length;
            x++
          ) {
            let extension =
              result.extensions['http://adlnet.gov/expapi/extension/variables'][
                x
              ]
            let val = {
              user: results[i].name,
              userId: results[i].userId,
              courseId: this.course.courseId,
              value: extension.value
            }
            let valArr = []
            valArr.push(val)

            if (this.variables.has(extension.name)) {
              valArr = this.variables.get(extension.name)
              valArr.push(val)
            }

            this.variables.set(extension.name, valArr)
            this.hasVariables = true
          }
        }
      }

      if (this.agentId != null) {
        let a = parseInt(this.agentId)

        interactions = Object.assign(
          [],
          _.filter(interactions, function(o) {
            if (o.userId != null && o.userId !== undefined) {
              return o.userId === a
            }
          })
        )
      }

      this.timeOptions = getDateOptionsFromInteractions(interactions)
      this.onTimeLine('all')

      interactions.forEach(element => {
        if (element.verb === 'failed' || element.verb === 'passed') {
          this.agentStatus = element.verb
        }
      })

      if (
        this.course.interactions != null &&
        this.course.interactions !== undefined
      ) {
        let sectionData = new Array()
        interactions.forEach(element => {
          if (element.duration != null && element.duration !== undefined) {
            element.duration = getDuration(element.duration)
          }

          if (element.section != null && element.section !== undefined) {
            sectionData.push(element)
          }

          if (this.agentId != null) {
            if (element.userId === parseInt(this.agentId)) {
              this.tableData.push(element)
            }
          } else {
            this.tableData.push(element)
          }
        })
        this.loading = false

        sectionData = _.orderBy(sectionData, ['timestamp'], ['asc'])
        this.tableData = sortDataByDate(this.tableData)
        this.sections = _.chain(sectionData)
          .filter(filterBy => filterBy.verb !== 'updated')
          .groupBy('section')
          .map((value, key) => ({ section: key, interactions: value }))
          .value()

        let duration = null
        let questionCount = 0
        let hotspotCount = 0
        let agentSet = new Multiset()

        this.sections.forEach((element, index, arr) => {
          this.sections[index].activities = _.chain(element.interactions)
            .groupBy('activity.id')
            .map((value, key) => ({
              name: value[0].activity.definition.name,
              interactions: value,
              type:
                value[0].activity &&
                value[0].activity.definition &&
                value[0].activity.definition.interactionType
                  ? value[0].activity.definition.interactionType
                  : 'interactions'
            }))
            .value()

          duration = null
          questionCount = 0
          hotspotCount = 0

          this.sections[index].activities.forEach(activity => {
            if (activity.type === 'interactions') {
              hotspotCount++
            } else if (activity.type === 'choice') {
              questionCount++
            }
          })
          interactions.forEach(interaction => {
            if (
              interaction.verb === 'experienced' &&
              element.section === interaction.definitionName
            ) {
              if (duration == null) {
                duration = moment.duration(interaction.duration)
              } else {
                duration = moment.duration(interaction.duration).add(duration)
              }
            }
          })

          this.sections[index].duration = getDuration(duration)
          this.sections[index].questions = questionCount
          this.sections[index].hotspots = hotspotCount
        })
      }
    },
    formatDuration(date) {
      return getDuration(date)
    }
  },
  computed: {
    myStyles() {
      return pieChartStyle
    },
    onlyAgent() {
      return this.agentId != null
    },
    pieCheckSize() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
          return 'font-size: 86px;'
          break
        case 'sm':
          return 'font-size: 146px;'
          break
        case 'md':
          return 'font-size: 226px;'
          break
        case 'lg':
          return 'font-size: 266px;'
          break
        case 'xl':
          return 'font-size: 286px;'
          break
        default:
          return 'font-size: 286px;'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-text-field {
  padding-top: 0px;
  margin-top: 0px;
}
.v-input__slot {
  margin-bottom: -14px !important;
}
.pie-chart {
  height: 200px;
  position: relative;
}
.list-space {
  padding: 5px;
}
.v-card__text {
  max-height: 70px;
}
.bubble-chart {
  max-height: 250px;
  height: 250px;
  width: 90%;
  position: relative;
}

.section-item-title {
  color: #0fa0df;
  font-weight: 500;
  font-size: 20px;
}

.section-title {
  color: #0fa0db;
  font-weight: 100;
}

.section-stats-display {
  display: inline-block;
  font-family: 'Roboto', 'Helvetica', 'Arial', 'sans-serif';
  text-align: left;
  font-weight: 100;
  font-size: 20px;
  color: #606060;
}
</style>
